import { useEffect, useState } from 'react'
import {
  useLazyGetAllUserSurveyGraphQuery,
  useLazyGetAllUserSurveyQuery,
} from '../../../../services/cellcast'
import { Spinner } from 'react-bootstrap'
import moment from 'moment-timezone'
import ReactApexChart from 'react-apexcharts'
import NodataYet from '../../../Components/NodataYet'
import { useLoadedData } from '../../../hooks/useGetLoadedData'
import ReactPaginate from 'react-paginate'

const Survey = () => {
  const [triggerFetch, setTriggerFetch] = useState(false)
  const {calculate, loadedData, setLoadedData} = useLoadedData()
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    source: '',
  })

  const [fetchSurvey, { isLoading, data, isFetching }] = useLazyGetAllUserSurveyQuery()
  const [fetchSurveyGraph, { isLoading: loadingGraph, data: dataGraph }] = useLazyGetAllUserSurveyGraphQuery();

  useEffect(() => {
    fetchSurvey(params);
    fetchSurveyGraph({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (triggerFetch && triggerFetch === true) {
      fetchSurvey(params);
    }
    setTriggerFetch(false)
  }, [triggerFetch])

  useEffect(() => {
    if (data) {
      const loadedData = calculate(
        data && data.data.current ? data.data.current : 1,
        params.limit,
        data && data.data.total ? data.data.total : 0
      )
      setLoadedData(loadedData)
    }
  }, [isLoading, isFetching])

  const perDay = dataGraph?.data?.[0]?.perDay ?? [];
  const series = [
    {
      name: 'Average',
      data: perDay.map((entry) => [entry.date, entry.average]),
    },
    {
      name: 'Total',
      data: perDay.map((entry) => [entry.date, entry.total]),
    },
  ];

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'area',
      stacked: false,
      height: 350,
      zoom: {
        type: 'x',
        enabled: false,
        autoScaleYaxis: true,
      },
      toolbar: {
        show: false,
        autoSelected: 'zoom',
      },
    },
    noData: {
      text: 'No data yet',
      align: 'center',
      verticalAlign: 'middle',
      style: {
        fontSize: '20px',
        color: '#a6a6b1',
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 4,
      hover: {
        sizeOffset: 4,
      },
    },
    title: {
      text: '',
      align: 'left',
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        inverseColors: false,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 90, 100],
      },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => val.toFixed(0),
      },
      title: {
        text: '',
      },
    },
    xaxis: {
      type: 'datetime',
      tickAmount: perDay.length,
      labels: {
        format: 'yyyy-MM-dd',
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM yyyy',
          day: 'yyyy-MM-dd',
        },
        rotate: -45,
        style: {
          fontSize: '12px',
        },
      },
      tooltip: {
        enabled: true,
      },
      title: {
        text: 'Date',
      },
    },
    tooltip: {
      shared: true,
      x: {
        format: 'yyyy-MM-dd',
      },
      y: {
        formatter: (val: number) => val.toFixed(0),
      },
    },
  };


  const survey: any = {
    series: series,
    options: options,
  }

  return (
    <>
      <div className={`card mb-5`}>
        <div className='card-header border-0 p-0 align-items-center mb-10'>
          <div className='d-flex flex-column w-100 text-center'>
            <h3 className='card-title flex-column'>
              <span className='card-label fw-bolder fs-2 mb-1'>User Survey</span>
              <span className='fw-normal text-muted fs-6'>
                Total: {dataGraph?.data?.[0]?.summary?.[0]?.total ?? 0} | Average: {dataGraph?.data?.[0]?.summary?.[0]?.average?.toFixed(1) ?? 0}
              </span>
            </h3>
          </div>
        </div>

        {loadingGraph ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '100px' }}
          >
            <Spinner />
          </div>
        ) : survey?.series.length > 0 ? (
          <div>
            <ReactApexChart
              options={survey.options}
              series={survey.series}
              type='area'
              height={375}
              width='100%'
            />
          </div>
        ) : (
          <NodataYet message={'No data yet'} customeHight='100' />
        )}
      </div>

      <div className={`card`}>
        <div className='card-header border-0 p-0 align-items-center mb-10'>
          <div className='d-flex flex-column w-100'>
            <h3 className='card-title align-items-start  flex-column'>
              <span className='card-label fw-bolder fs-2 mb-1'>User Survey Log</span>
            </h3>
          </div>
        </div>
        <div className='card-body py-3'>
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-400 text-muted '>
                  <th className='ps-4 text-start'>Name</th>
                  <th className='ps-4 text-start'>Email</th>
                  <th className='ps-4 text-start'>Score</th>
                  <th className='ps-4 text-start'>Note</th>
                  <th className='ps-4 text-start'>Created</th>
                </tr>
              </thead>
              <tbody className='text-gray-600'>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className='text-center'>
                      <Spinner />
                    </td>
                  </tr>
                ) : (
                  <>
                    {data?.data?.items?.map(item => (
                      <tr className='text-muted '>
                        <td>
                          {item?.name}
                        </td>
                        <td>
                          {item?.email}
                        </td>
                        <td>
                          {item?.survey?.score}
                        </td>
                        <td>
                          {item?.survey?.note ?? '-'}
                        </td>
                        <td className='ps-4 text-start'>
                          {moment(item?.survey?.createdAt).format('DD/MM/YYYY')}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <div className='abcd'>
            <select
              onChange={(e: any) => {
                setParams({ ...params, limit: e.target.value })
                setTriggerFetch(true)
              }}
              value={params.limit || 10}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            {isLoading
              ? '. . . . .'
              : `Show ${loadedData.startLoad} - ${loadedData.endLoad} of ${data?.data?.total ? data?.data?.total : 0
              } Survey`}
            <ReactPaginate
              previousLabel='<'
              nextLabel='>'
              pageCount={data?.data?.totalPages as number}
              onPageChange={(e: any) => {
                setParams({ ...params, page: e.selected + 1 })
                setTriggerFetch(true)
              }}
              containerClassName={'pagination'}
              previousLinkClassName={'pagination__link'}
              nextLinkClassName={'pagination__link'}
              disabledClassName={'pagination__link--disabled'}
              activeClassName={'pagination__link--active'}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default Survey
